import { useMemo } from 'react'

import {
  CatalogLinksScreen,
  linkBlockerOf,
  linkCompleted,
  linkDraftPatched,
  linkOperationAssigned,
  listResourceChosen,
  makeSelectCatalogLinksVm,
  newUuid,
  platformCatalogLink,
  platformCatalogLinks,
  platformCatalogUnlink,
  platformResources,
  type CatalogLinksActions,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { CONNECTION_FORMS } from '../../composition/connectionForms'

const selectResources = platformResources.select()
const kindTitle = (kind: string) => CONNECTION_FORMS.titleOf(kind)

/**
 * Binding окна «Связи товаров» (FIX-A2). Ключ операции — намерение человека:
 * закрепляется за черновиком при первом нажатии «Связать» и переиспользуется
 * при повторе того же черновика (обрыв ответа — тот же итог, без второго
 * связывания). Правка черновика даёт новое намерение.
 */
export function CatalogLinksBinding() {
  const dispatch = useAppDispatch()
  const intent = useAppSelector((state: RootState) => state.platform.catalogLinks)
  const listArgs = useMemo(() => ({ resourceId: intent.listResourceId }), [intent.listResourceId])
  const resourcesSubscription = platformResources.useQuerySubscription()
  const listSubscription = platformCatalogLinks.useQuerySubscription(listArgs, { skip: intent.listResourceId === '' })
  const [link, linkResult] = platformCatalogLink.useMutation({ fixedCacheKey: 'catalog-link' })
  const [unlink, unlinkResult] = platformCatalogUnlink.useMutation({ fixedCacheKey: 'catalog-unlink' })

  const selectList = useMemo(() => platformCatalogLinks.select(listArgs), [listArgs])
  const resources = useAppSelector(selectResources)
  const list = useAppSelector(selectList)
  const selectVm = useMemo(() => makeSelectCatalogLinksVm(), [])
  const vm = selectVm({
    intent,
    resources,
    list: intent.listResourceId === '' ? null : list,
    link: linkResult,
    unlink: unlinkResult,
    kindTitle,
  })

  const refetchResources = resourcesSubscription.refetch
  const refetchList = listSubscription.refetch
  const actions = useMemo<CatalogLinksActions>(
    () => ({
      patchDraft: (patch) => {
        linkResult.reset()
        dispatch(linkDraftPatched(patch))
      },
      link: () => {
        if (linkBlockerOf(intent) !== null) return
        const draft = intent.draft
        // Повтор того же черновика — тот же ключ: сервер вернёт исполненный итог, а не свяжет второй раз.
        const operationId = draft.operationId ?? newUuid()
        if (draft.operationId === null) dispatch(linkOperationAssigned(operationId))
        void link({
          operationId,
          reason: draft.reason.trim(),
          productName: draft.productName.trim() === '' ? null : draft.productName.trim(),
          cards: [
            { resourceId: draft.firstResourceId, remoteKey: draft.firstKey.trim() },
            { resourceId: draft.secondResourceId, remoteKey: draft.secondKey.trim() },
          ],
        })
          .unwrap()
          .then(() => dispatch(linkCompleted(operationId)))
          .catch(() => undefined)
      },
      chooseListResource: (resourceId) => {
        unlinkResult.reset()
        dispatch(listResourceChosen(resourceId))
      },
      unlink: (productId, resourceId, reason, operationId) => {
        void unlink({ operationId, productId, resourceId, reason })
      },
      reload: () => {
        void refetchResources()
        if (intent.listResourceId !== '') void refetchList()
      },
    }),
    [dispatch, intent, link, linkResult, refetchList, refetchResources, unlink, unlinkResult],
  )

  return <CatalogLinksScreen vm={vm} actions={actions} />
}
